<template>
  <div>
    <!-- Loading Skeleton — never show the empty state before the saved words arrive -->
    <div v-if="loading">
      <v-skeleton-loader type="heading" class="mb-4"></v-skeleton-loader>
      <v-skeleton-loader type="paragraph" class="mb-4"></v-skeleton-loader>
    </div>

    <!-- Load failed: editing is blocked so an empty list cannot be saved over the real one -->
    <v-alert v-else-if="loadError" type="error" variant="tonal" class="mb-4">
      <div class="d-flex align-center" style="gap: 1rem">
        <span>{{ $t('wakeWordLoadFailed') }}</span>
        <v-spacer></v-spacer>
        <v-btn color="#4E3982" variant="outlined" class="text-none" @click="$emit('retry')">{{ $t('wakeWordRetry') }}</v-btn>
      </div>
    </v-alert>

    <div v-else class="mb-4">
      <div class="d-flex align-center mb-1">
        <h3 class="language-config-title mr-3">{{ $t('wakeWord') }}</h3>
        <span class="text-caption">{{ $t('wakeWordTotal', { n: modelValue.length }) }}</span>
      </div>
      <p class="text-caption text-medium-emphasis mb-4">{{ $t('wakeWordDescription') }}</p>

      <!-- No configuration yet: nothing to save the words onto -->
      <v-alert v-if="canEdit && !hasConfiguration" type="info" variant="tonal" density="compact" class="mb-4">
        {{ $t('wakeWordNeedConfig') }}
      </v-alert>

      <!-- Add -->
      <template v-if="editable">
        <label class="wake-word-label">{{ $t('wakeWordAddLabel') }}</label>
        <div class="d-flex align-start mt-1" style="gap: 0.75rem">
          <textarea
            v-model="inputText"
            :rows="inputRows"
            :placeholder="$t('wakeWordAddPlaceholder')"
            :disabled="saving"
            class="custom-input wake-word-input"
            @keydown.enter.exact.prevent="addWords"
          ></textarea>
          <v-btn
            color="#4E3982"
            variant="flat"
            style="height: 45px; border-radius: 12px; text-transform: none"
            :disabled="!inputText.trim() || saving"
            :loading="saving"
            @click="addWords"
          >
            <v-icon start size="small">mdi-plus</v-icon>
            {{ $t('wakeWordAdd') }}
          </v-btn>
        </div>
        <div v-if="summary" class="text-caption mt-1" :class="summary.added.length ? 'wake-word-summary' : 'text-warning'">
          <v-icon size="small" :icon="summary.added.length ? 'mdi-check-circle' : 'mdi-alert-circle'"></v-icon>
          {{ summaryText }}
        </div>
      </template>

      <!-- Search + count -->
      <div class="d-flex align-center mt-5 mb-3" style="gap: 1rem">
        <input v-model="searchText" :placeholder="$t('wakeWordSearch')" class="custom-input" style="max-width: 320px" />
        <v-spacer></v-spacer>
        <span class="text-caption">
          {{ searchKey ? $t('wakeWordShowing', { shown: visibleEntries.length, n: modelValue.length }) : $t('wakeWordTotal', { n: modelValue.length }) }}
        </span>
      </div>

      <!-- Words -->
      <div class="wake-word-box">
        <div v-if="modelValue.length === 0" class="text-center text-medium-emphasis py-6">{{ $t('wakeWordEmpty') }}</div>
        <div v-else-if="visibleEntries.length === 0" class="text-center text-medium-emphasis py-6">{{ $t('wakeWordNotFound') }}</div>
        <div v-else class="d-flex flex-wrap" style="gap: 0.5rem">
          <template v-for="entry in visibleEntries" :key="entry.word">
            <div v-if="editingIndex === entry.index" class="wake-word-edit">
              <input
                ref="editInput"
                v-model="editingValue"
                class="custom-input"
                :class="{ 'input-error': editError }"
                :disabled="saving"
                @keydown.enter.prevent="commitEdit"
                @keydown.esc.prevent="cancelEdit"
                @blur="onEditBlur"
              />
              <div v-if="editError" class="text-caption text-error">{{ editError }}</div>
            </div>
            <!-- :model-value keeps the chip visible after × — it only goes when the confirmed delete is saved -->
            <v-chip
              v-else
              :model-value="true"
              color="#4E3982"
              variant="tonal"
              :closable="editable"
              :disabled="saving"
              :title="editable ? $t('wakeWordEditHint') : undefined"
              @click="startEdit(entry.index)"
              @click:close="removeWord(entry.index)"
            >
              {{ entry.word }}
            </v-chip>
          </template>
        </div>
      </div>

      <div class="d-flex justify-end mt-3">
        <v-btn
          color="#4E3982"
          variant="outlined"
          style="height: 40px; border-radius: 12px; text-transform: none"
          prepend-icon="mdi-content-copy"
          :disabled="modelValue.length === 0"
          @click="copyAll"
        >
          {{ $t('wakeWordCopyAll') }}
        </v-btn>
      </div>
    </div>

    <v-snackbar v-model="snackbar.show" :color="snackbar.color" :timeout="2500" location="bottom">{{ snackbar.text }}</v-snackbar>
  </div>
</template>

<script>
import {
  WAKE_WORD_MAX_COUNT,
  WAKE_WORD_MAX_LENGTH,
  mergeWakeWords,
  normalizeWakeWord,
  splitWakeWordInput,
  validateWakeWordEdit,
} from '@/utils/wakeWords';

const CONFIRM_LIST_LIMIT = 20;

// #786 — Mobile Configurations → Wake word. Every add / edit / delete asks for confirmation and is then saved
// straight away on its own (PUT MobileConfiguration/WakeWords/{id}, which also writes the History Log) —
// the page's Save button does not touch the wake words.
export default {
  name: 'WakeWordTab',
  props: {
    modelValue: {
      type: Array,
      default: () => [],
    },
    mobileConfigurationId: {
      type: Number,
      default: null,
    },
    loading: {
      type: Boolean,
      default: false,
    },
    loadError: {
      type: Boolean,
      default: false,
    },
    canEdit: {
      type: Boolean,
      default: false,
    },
  },
  emits: ['update:modelValue', 'retry'],
  data() {
    return {
      inputText: '',
      searchText: '',
      summary: null,
      saving: false,
      editingIndex: null,
      editingValue: '',
      editError: '',
      snackbar: { show: false, text: '', color: '#4E3982' },
    };
  },
  computed: {
    hasConfiguration() {
      return this.mobileConfigurationId !== null && this.mobileConfigurationId !== undefined;
    },
    editable() {
      return this.canEdit && this.hasConfiguration;
    },
    inputRows() {
      return Math.min(Math.max(this.inputText.split('\n').length, 1), 6);
    },
    searchKey() {
      return normalizeWakeWord(this.searchText);
    },
    visibleEntries() {
      const entries = this.modelValue.map((word, index) => ({ word, index }));
      if (!this.searchKey) return entries;
      return entries.filter((entry) => entry.word.includes(this.searchKey));
    },
    summaryText() {
      if (!this.summary) return '';
      const parts = [
        this.summary.added.length ? this.$t('wakeWordAddedSummary', { n: this.summary.added.length }) : this.$t('wakeWordNoNew'),
      ];
      if (this.summary.duplicates) parts.push(this.$t('wakeWordSkippedDuplicate', { n: this.summary.duplicates }));
      if (this.summary.tooLong) parts.push(this.$t('wakeWordSkippedTooLong', { n: this.summary.tooLong, max: WAKE_WORD_MAX_LENGTH }));
      if (this.summary.overLimit) parts.push(this.$t('wakeWordSkippedOverLimit', { n: this.summary.overLimit, max: WAKE_WORD_MAX_COUNT }));
      return parts.join(' · ');
    },
  },
  watch: {
    // Typing again clears the last summary
    inputText(value) {
      if (value) this.summary = null;
    },
  },
  methods: {
    async addWords() {
      if (!this.editable || this.saving || !this.inputText.trim()) return;

      const input = this.inputText;
      const result = mergeWakeWords(this.modelValue, splitWakeWordInput(input));
      if (result.added.length === 0) {
        this.summary = result;
        return;
      }

      const confirmed = await this.confirm(
        this.$t('wakeWordConfirmAdd', { n: result.added.length }),
        this.listForConfirm(result.added)
      );
      if (!confirmed) return; // keep the typed text so it can be fixed

      const saved = await this.persist(result.words);
      if (!saved) return;

      this.inputText = '';
      this.summary = result;
      // Make the new words visible
      this.searchText = '';
    },

    async removeWord(index) {
      if (!this.editable || this.saving) return;

      const word = this.modelValue[index];
      const confirmed = await this.confirm(this.$t('wakeWordConfirmRemove'), word, 'warning');
      if (!confirmed) return;

      await this.persist(this.modelValue.filter((_, i) => i !== index));
    },

    startEdit(index) {
      if (!this.editable || this.saving) return;
      this.editingIndex = index;
      this.editingValue = this.modelValue[index];
      this.editError = '';
      this.$nextTick(() => {
        const input = Array.isArray(this.$refs.editInput) ? this.$refs.editInput[0] : this.$refs.editInput;
        input?.focus();
        input?.select();
      });
    },

    async commitEdit() {
      if (this.editingIndex === null || this.saving) return;

      const index = this.editingIndex;
      const oldWord = this.modelValue[index];
      const result = validateWakeWordEdit(this.modelValue, index, this.editingValue);
      if (result.error) {
        this.editError = this.editErrorText(result.error);
        return;
      }
      if (result.word === oldWord) {
        this.cancelEdit();
        return;
      }

      // Close the editor first — the dialog takes the focus and would blur it anyway
      this.cancelEdit();
      const confirmed = await this.confirm(this.$t('wakeWordConfirmEdit'), `${oldWord} → ${result.word}`);
      if (!confirmed) return;

      const words = [...this.modelValue];
      words[index] = result.word;
      await this.persist(words);
    },

    onEditBlur() {
      if (!this.saving) this.cancelEdit();
    },

    cancelEdit() {
      this.editingIndex = null;
      this.editingValue = '';
      this.editError = '';
    },

    editErrorText(error) {
      if (error === 'duplicate') return this.$t('wakeWordEditDuplicate');
      if (error === 'tooLong') return this.$t('wakeWordEditTooLong', { max: WAKE_WORD_MAX_LENGTH });
      return this.$t('wakeWordEditEmpty');
    },

    listForConfirm(words) {
      const shown = words.slice(0, CONFIRM_LIST_LIMIT).join(', ');
      return words.length > CONFIRM_LIST_LIMIT ? `${shown} … (+${words.length - CONFIRM_LIST_LIMIT})` : shown;
    },

    // `text` (not `html`) so the admin's words are never rendered as markup
    // icon: 'info' (purple) for add / edit, 'warning' for delete
    async confirm(title, text, icon = 'info') {
      const result = await this.$swal.fire({
        title,
        text,
        icon,
        iconColor: icon === 'info' ? '#4E3982' : undefined,
        showCancelButton: true,
        confirmButtonText: this.$t('wakeWordConfirmSave'),
        cancelButtonText: this.$t('cancel'),
        confirmButtonColor: '#4E3982',
      });
      return result.isConfirmed;
    },

    // Saves the whole list; on success the parent's list becomes what the server stored
    persist(words) {
      this.saving = true;
      return new Promise((resolve) => {
        this.$call.SaveMobileConfigurationWakeWords(
          this.mobileConfigurationId,
          { wakeWords: words },
          (response) => {
            this.saving = false;
            if (response && (response.Status === 'Successful' || response.status === 0)) {
              const stored = response.data ?? response.Data ?? words;
              this.$emit('update:modelValue', [...stored]);
              this.snackbar = { show: true, text: this.$t('wakeWordSaved'), color: '#4E3982' };
              resolve(true);
            } else {
              this.showSaveError(response?.message || response?.Message);
              resolve(false);
            }
          },
          (error) => {
            this.saving = false;
            console.error('Save wake words error:', error);
            this.showSaveError(error?.response?.data?.message || error?.response?.data?.Message);
            resolve(false);
          }
        );
      });
    },

    showSaveError(detail) {
      this.$swal.fire({
        title: this.$t('error'),
        text: detail ? `${this.$t('wakeWordSaveFailed')} (${detail})` : this.$t('wakeWordSaveFailed'),
        icon: 'error',
        confirmButtonText: this.$t('ok'),
      });
    },

    async copyAll() {
      // Always every word, even while a search filter is on
      try {
        if (!navigator.clipboard) throw new Error('Clipboard API is not available');
        await navigator.clipboard.writeText(this.modelValue.join('\n'));
        this.snackbar = { show: true, text: this.$t('wakeWordCopied', { n: this.modelValue.length }), color: '#4E3982' };
      } catch (error) {
        console.error('Copy wake words error:', error);
        this.snackbar = { show: true, text: this.$t('wakeWordCopyFailed'), color: 'error' };
      }
    },
  },
};
</script>

<style scoped>
.language-config-title {
  font-size: 24px;
  font-weight: 600;
  color: #333;
}

.wake-word-label {
  font-size: 14px;
  font-weight: 500;
  color: #333;
}

.custom-input {
  width: 100%;
  height: 45px;
  padding: 8px 16px;
  border: 1px solid #bec2ca;
  border-radius: 16px;
  background-color: white;
  font-size: 14px;
  outline: none;
  transition: border-color 0.2s ease;
}

textarea.custom-input {
  height: auto;
  min-height: 45px;
  padding: 10px 16px;
  resize: none;
}

.custom-input:focus {
  border-color: #643990;
  outline: none;
}

.custom-input::placeholder {
  color: #9e9e9e;
}

.wake-word-summary {
  color: #4e3982;
}

.input-error {
  border-color: #c62828 !important;
}

.wake-word-box {
  max-height: 400px;
  overflow-y: auto;
  padding: 12px;
  border: 1px solid #e0e0e0;
  border-radius: 16px;
}

.wake-word-edit {
  min-width: 220px;
}

.wake-word-edit .custom-input {
  height: 32px;
  padding: 4px 12px;
}
</style>
